import { type RegisteredRouter, useRouterState } from '@tanstack/react-router';
import type { FileRouteTypes } from '@/routeTree.gen';

/**
 * The pages the cookie consent banner is not allowed to open on.
 *
 * Two kinds, for the same reason: the banner opens as a centred modal with a backdrop, so on a page nobody
 * is going to answer it on, it is a sheet of nothing sitting over the content.
 *
 * The legal notice, the privacy policy and the cookie policy — on a portal and on the landing alike, for
 * the two of the three a portal serves. These are pages a donor is reading rather than acting on, and the
 * banner sits on top of the text. They are also where the banner's own "Privacy policy", "Legal notice"
 * and "More information" links lead, which makes covering the page the expected outcome of following them
 * rather than an unlucky one — the cookie policy most of all, since it is the page a donor opens precisely
 * to work out what they are being asked.
 *
 * The live counter, which has no reader at all: it is a full-screen counter meant to be left open for
 * weeks on a wall display, with none of the site chrome and nobody at the keyboard to dismiss anything. An
 * unanswered banner there is permanent, and it covers the one thing the screen exists to show.
 *
 * The banner is still reachable from every legal page: the footer's "Privacy settings" opens the settings
 * layer, which is the layer that can actually answer the question. Only the first layer is withheld. See
 * `syncUsercentricsBannerVisibility`. The live counter has no footer and so no way back to it, which is
 * the point — nothing on that screen is consented to, because nothing on it is a donor.
 *
 * Route ids rather than paths: the legal pages are each served under a translated slug per locale
 * (`/politica-de-privacidad`, `/informativa-sulla-privacy`, …), and matching those by hand would silently
 * stop covering a locale the moment one is added. Typed as generated route ids so renaming a route file
 * breaks the build here instead of quietly re-admitting the banner.
 */
const BANNER_FREE_ROUTE_IDS: readonly FileRouteTypes['id'][] = [
	'/$portalId/legal-disclaimer',
	'/$portalId/privacy-policy',
	'/www/cookies',
	'/www/legal-disclaimer',
	'/www/live-counter',
	'/www/privacy-policy',
];

/**
 * Whether the page currently rendered is one of {@link BANNER_FREE_ROUTE_IDS}.
 *
 * Read from the router's matches rather than from the URL, so it follows client-side navigation: entering
 * and leaving these pages is what the consent banner has to react to. Used only from effects, never in the
 * markup, so it cannot make the server and the client disagree about what to render.
 */
export const useBannerFreeRoute = (): boolean =>
	useRouterState<RegisteredRouter, boolean>({
		select: (state) => state.matches.some((match) => BANNER_FREE_ROUTE_IDS.some((id) => id === match.routeId)),
	});
